import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { db, Patient } from '../lib/db';
import VoiceRecorder from '../components/VoiceRecorder';
import SBARCard from '../components/SBARCard';

interface Baseline {
  id: string;
  patient_id: string;
  role: string;
  created_by: string;
  created_at: string;
  transcript: string;
  sbar: {
    situation: string;
    background: string;
    assessment: string;
    recommendation: string;
  };
}

export default function PatientDetail() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [baselines, setBaselines] = useState<Baseline[]>([]);

  useEffect(() => {
    const loadPatient = async () => {
      if (!patientId) return;

      const p = await db.patients.get(patientId);
      if (p) {
        setPatient(p);
      }
      setIsLoading(false);
    };

    const loadBaselines = async () => {
      if (!patientId) return;

      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api-production-7df0.up.railway.app';
      const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

      // Skip API call in demo mode
      if (isDemoMode) {
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/patients/${patientId}/baselines`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setBaselines(data);
        }
      } catch (error) {
        console.error('Failed to load baselines:', error);
      }
    };

    loadPatient();
    loadBaselines();
  }, [patientId]);

  if (isLoading) {
    return (
      <div className="page">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-peacockTeal-500"></div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="page">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Patient Not Found</h2>
          <button onClick={() => navigate('/priority')} className="btn btn-primary">
            Back to Priority Patients
          </button>
        </div>
      </div>
    );
  }

  const priorityConfig =
    patient.priority === 'High'
      ? { bg: 'bg-red-50', border: 'border-red-300', badge: 'badge-high' }
      : patient.priority === 'Medium'
      ? { bg: 'bg-yellow-50', border: 'border-yellow-300', badge: 'badge-medium' }
      : { bg: 'bg-green-50', border: 'border-green-300', badge: 'badge-low' };

  return (
    <div className="page">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/priority')}
          className="mb-4 flex items-center text-peacockTeal-600 hover:text-peacockTeal-700 font-medium"
        >
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Priority Patients
        </button>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg"
              style={{
                background: 'linear-gradient(135deg, var(--color-primary-500) 0%, var(--color-primary-600) 100%)',
              }}
            >
              {patient.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{patient.name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <span className={priorityConfig.badge}>{patient.priority} Priority</span>
                {patient.mrn && (
                  <span className="text-sm text-gray-600">MRN: {patient.mrn}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Patient Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Demographics */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <svg className="w-6 h-6 mr-2 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Demographics
          </h2>
          <div className="space-y-3">
            {patient.dob && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Date of Birth</dt>
                <dd className="text-base text-gray-900">{new Date(patient.dob).toLocaleDateString()}</dd>
              </div>
            )}
            {patient.gender && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Gender</dt>
                <dd className="text-base text-gray-900">{patient.gender}</dd>
              </div>
            )}
            {patient.unit && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Unit</dt>
                <dd className="text-base text-gray-900">{patient.unit}</dd>
              </div>
            )}
            {patient.room && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Room</dt>
                <dd className="text-base text-gray-900">{patient.room}</dd>
              </div>
            )}
          </div>
        </div>

        {/* Clinical Info */}
        <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <svg className="w-6 h-6 mr-2 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Clinical Information
          </h2>
          <div className="space-y-3">
            {patient.diagnoses && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Diagnoses</dt>
                <dd className="text-base text-gray-900">{patient.diagnoses}</dd>
              </div>
            )}
            {patient.allergies && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Allergies</dt>
                <dd className="text-base text-red-600 font-medium">{patient.allergies}</dd>
              </div>
            )}
            {patient.points !== undefined && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Honors Points</dt>
                <dd className="text-base text-gradient font-bold">{patient.points.toLocaleString()} Points</dd>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voice Recorder Section */}
      <div className="mb-6">
        <VoiceRecorder patientId={patient.id} patientName={patient.name} />
      </div>

      {/* Recent SBAR Reports */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-peacockTeal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Recent SBAR Reports
        </h2>
        {baselines.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No SBAR reports yet. Record a voice note to generate your first report.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {baselines.map((baseline) => (
              <SBARCard
                key={baseline.id}
                sbar={baseline.sbar}
                timestamp={baseline.created_at}
                recordedBy={baseline.role}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
